import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isStoreConnected, setIsStoreConnected] = useState(false);

  useEffect(() => {
    // Check if user is logged in using existing token
    const token = localStorage.getItem('token');
    if (token) {
      // Try to get user info from existing API
      api.get('/api/auth/me')
        .then(response => {
          if (response.data && response.data.user) {
            setUser(response.data.user);
            setIsAuthenticated(true);
            setIsSubscribed(response.data.user.subscriptionStatus === 'active' || response.data.user.hasActiveSubscription);
            setIsStoreConnected(response.data.user.stores && response.data.user.stores.length > 0);
          } else {
            // Fallback to demo user if API fails
            const demoUser = {
              id: 'demo-user',
              email: 'demo@cartresq.com',
              stores: [
                {
                  storeUrl: 'https://demo-store.com',
                  platform: 'woocommerce',
                  isActive: true
                }
              ]
            };
            setUser(demoUser);
            setIsAuthenticated(true);
            setIsSubscribed(false);
            setIsStoreConnected(true);
          }
        })
        .catch(error => {
          console.log('Auth check failed, using demo user:', error);
          // Fallback to demo user
          const demoUser = {
            id: 'demo-user',
            email: 'demo@cartresq.com',
            stores: [
              {
                storeUrl: 'https://demo-store.com',
                platform: 'woocommerce',
                isActive: true
              }
            ]
          };
          setUser(demoUser);
          setIsAuthenticated(true);
          setIsSubscribed(false);
          setIsStoreConnected(true);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    // Mock login for demo
    const mockUser = {
      id: 'demo-user',
      email: email,
      stores: [
        {
          storeUrl: 'https://demo-store.com',
          platform: 'woocommerce',
          isActive: true
        }
      ]
    };
    
    localStorage.setItem('token', 'demo-token');
    setUser(mockUser);
    setIsAuthenticated(true);
    setIsSubscribed(false);
    setIsStoreConnected(true);
    return mockUser;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setIsAuthenticated(false);
    setIsSubscribed(false);
    setIsStoreConnected(false);
  };

  const value = {
    user,
    isAuthenticated,
    isSubscribed,
    isStoreConnected,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
